import subjectAdapter from 'backend/lib/adapters/subjectAdapter'
import subjectTreeAdapter from 'backend/lib/adapters/subjectTreeAdapter'
import searchService from 'backend/services/searchService'
import urApiService from 'backend/services/urApiService'
import { SearchParams } from 'backend/services/types/urApiService.types'
import BackLink from 'components/BackLink/BackLink'
import HorizontalFilterContainer from 'components/HorizontalFilterContainer/HorizontalFilterContainer'
import IntroductionBanner from 'components/IntroductionBanner/IntroductionBanner'
import Layout from 'components/Layout/Layout'
import OgMetadata from 'components/OgMetadata/OgMetadata'
import SubjectBlock from 'components/SubjectBlock/SubjectBlock'
import { DataLayerPageType, FilterType, SortOptions } from 'frontend/enums'
import { CategoryItem, SearchResult, Subject } from 'frontend/types'
import usePageTracking from 'frontend/utils/hooks/usePageTracking'
import { getOrigin } from 'frontend/utils/urlHelpers'
import omit from 'lodash/omit'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import styles from 'styles/SubjectPage.module.css'
import { queryParamTransformer } from 'utils/transformers'
import { getCategoriesForMenu } from 'utils/categoryUtil'

interface Props {
  subject: Subject
  initialSearchParams: SearchParams
  initialSearchResult: SearchResult
  highlightedCategories: CategoryItem[]
  minorityLanguageCategories: CategoryItem[]
  origin: string
}

const SubjectPage = ({
  subject,
  initialSearchParams,
  initialSearchResult,
  highlightedCategories,
  minorityLanguageCategories,
  origin,
}: Props) => {
  const { name, ogTitle, seoTitle, description } = subject

  const additionalData = { subject: name }
  usePageTracking(DataLayerPageType.SUBJECT_PAGE, additionalData)

  const url = `${origin}/amne${subject.route}`

  return (
    <>
      <Head>
        <title>{seoTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
      </Head>
      <OgMetadata
        title={ogTitle}
        description={description}
        type="article"
        sharePath={url}
      />
      <Layout
        categories={highlightedCategories}
        minorityLanguageCategories={minorityLanguageCategories}
      >
        <div className={styles.backLinkContainer}>
          <BackLink url={'/amne'} title="Alla ämnen" />
          {subject.breadCrumbs.map(breadCrumb => (
            <BackLink
              url={`/amne${breadCrumb.route}`}
              title={breadCrumb.name}
              key={breadCrumb.route}
            />
          ))}
        </div>
        {subject.childSubjects.length > 0 && (
          <>
            <IntroductionBanner title={name}>
              <p>{description}</p>
            </IntroductionBanner>
            <div className={styles.subjectPageWrapper}>
              <SubjectBlock
                isChildSubject
                title={`Inom ${subject.name.toLowerCase()}`}
                itemList={subject.childSubjects}
                blockTracking={{
                  blockIndex: 1,
                  blockType: 'subjects',
                  blockTitle: `Inom ${name.toLocaleLowerCase()}`,
                }}
              />
            </div>
          </>
        )}
        <HorizontalFilterContainer
          filterHeader={
            subject.childSubjects.length > 0
              ? {
                  headingLevel: 'h2',
                  description: '',
                  title: `Filtrera ${name}`,
                }
              : {
                  headingLevel: 'h1',
                  description,
                  title: name,
                }
          }
          blockTracking={{
            blockIndex: 2,
            blockType: 'carpet',
            blockTitle: subject.name,
          }}
          initialSearchParams={initialSearchParams}
          initialSearchResult={initialSearchResult}
          hiddenFilters={[
            FilterType.SCHOOL_SUBJECT,
            FilterType.KNOWLEDGE_AREA,
            FilterType.SORT,
          ]}
          excludedTags={['subject_tree']}
        />
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ctx => {
  const { query, params } = ctx
  let subject: Subject

  if (!params?.subjectPath) {
    return {
      notFound: true,
    }
  }

  const subjectTree = subjectTreeAdapter.adapt(
    await urApiService.getSubjectTree(),
  )

  try {
    subject = subjectAdapter.adapt(subjectTree, params.subjectPath as string[])
  } catch (error) {
    if ((error as Error).message === 'Route not in subject tree') {
      return {
        notFound: true,
      }
    }

    throw error
  }

  const initialSearchParams = {
    sort: SortOptions.MOST_VIEWED,
    subject_tree: subject.urApiQueryName,
    ...omit(queryParamTransformer(query, 'bracket'), ['subjectPath']),
  }

  const initialSearchResult = await searchService.search(
    initialSearchParams,
    'extended',
  )

  const origin = getOrigin(ctx)

  const { highlightedCategories, minorityLanguageCategories } =
    await getCategoriesForMenu()

  return {
    props: {
      initialSearchResult,
      initialSearchParams,
      subject,
      highlightedCategories,
      minorityLanguageCategories,
      origin,
    },
  }
}

export default SubjectPage
